import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import { formatIST } from "../../utils/dateUtils";
import Pagination from "../../components/admin/Pagination";
import { useAdminToast } from "../../context/AdminToastContext";

interface StuckDeal {
  id: string;
  deal_id?: string;
  job_id?: string | null;
  dealer_id?: string | null;
  stage?: string;
  error_code?: string;
  error_message?: string;
  error_detail?: Record<string, any> | null;
  deal_snapshot?: Record<string, any> | null;
  source?: string;
  created_at?: string;
}

const SOURCE_LABEL: Record<string, string> = {
  analysis_failed: "Analysis failed",
  seller_type_mismatch: "Seller-type mismatch",
  stale_reap: "Stalled / reaped",
};

export default function AdminStuckDealsPage() {
  const [records, setRecords] = useState<StuckDeal[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [detailTarget, setDetailTarget] = useState<StuckDeal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StuckDeal | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.listStuckDeals({ page, page_size: pageSize });
      setRecords(data.items as StuckDeal[]);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to load stuck deals:", err);
      setError("Failed to load stuck deals.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await adminService.deleteStuckDeal(deleteTarget.id);
      setDeleteTarget(null);
      if (detailTarget?.id === deleteTarget.id) setDetailTarget(null);
      await load();
    } catch (err) {
      console.error("Failed to delete stuck-deal record:", err);
      setError("Failed to delete this record.");
    } finally {
      setBusyId(null);
    }
  };

  const titleOf = (item: StuckDeal) =>
    item.deal_snapshot?.title || item.deal_snapshot?.ai_extracted?.title || item.deal_id || "Untitled deal";

  return (
    <div className="p-6">
      <AdminPageHeader
        className="mb-4"
        title="Stuck Deals"
        subtitle="Deals the AI-analysis pipeline abandoned and deleted — kept here for visibility into what happened and why."
        actions={
          <button
            onClick={() => void load()}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            Refresh
          </button>
        }
      />

      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : records.length === 0 ? (
        <p className="text-sm text-gray-400">No stuck deals recorded.</p>
      ) : (
        <div className="space-y-3">
          {records.map((item) => (
            <div key={item.id} className="rounded-lg border border-gray-700 bg-gray-900 p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{titleOf(item)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {SOURCE_LABEL[item.source || ""] || item.source} · Stage: {item.stage || "—"} · Error: {item.error_code || "—"}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate max-w-2xl">{item.error_message}</p>
                  {item.created_at && (
                    <p className="text-[11px] text-gray-600 mt-1">Recorded {formatIST(item.created_at)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setDetailTarget(item)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-700 text-white hover:bg-gray-600"
                  >
                    View details
                  </button>
                  <button
                    onClick={() => setDeleteTarget(item)}
                    disabled={busyId === item.id}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600/80 text-white hover:bg-red-600 disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}

      {detailTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-sm font-bold text-white mb-1">{titleOf(detailTarget)}</h3>
            <p className="text-xs text-gray-400 mb-3">
              {SOURCE_LABEL[detailTarget.source || ""] || detailTarget.source} · Stage: {detailTarget.stage || "—"}
              {detailTarget.created_at ? ` · Recorded ${formatIST(detailTarget.created_at)}` : ""}
            </p>
            <div className="text-xs text-gray-300 space-y-1 mb-4">
              <p><span className="text-gray-500">deal_id:</span> {detailTarget.deal_id || "—"}</p>
              <p><span className="text-gray-500">job_id:</span> {detailTarget.job_id || "—"}</p>
              <p><span className="text-gray-500">dealer_id:</span> {detailTarget.dealer_id || "—"}</p>
              <p><span className="text-gray-500">error_code:</span> {detailTarget.error_code || "—"}</p>
              <p><span className="text-gray-500">error_message:</span> {detailTarget.error_message || "—"}</p>
            </div>
            {detailTarget.error_detail && (
              <>
                <p className="text-xs font-semibold text-gray-400 mb-1">Error detail</p>
                <pre className="text-[11px] text-gray-300 bg-gray-800 rounded-lg p-3 mb-4 overflow-x-auto">
                  {JSON.stringify(detailTarget.error_detail, null, 2)}
                </pre>
              </>
            )}
            <p className="text-xs font-semibold text-gray-400 mb-1">Deal snapshot at failure time</p>
            <pre className="text-[11px] text-gray-300 bg-gray-800 rounded-lg p-3 mb-4 overflow-x-auto">
              {JSON.stringify(detailTarget.deal_snapshot ?? {}, null, 2)}
            </pre>
            <button
              onClick={() => setDetailTarget(null)}
              className="w-full py-2 text-xs font-medium rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-5 max-w-sm w-full">
            <h3 className="text-sm font-bold text-white mb-1">Delete this record?</h3>
            <p className="text-xs text-gray-400 mb-4">
              This permanently removes the stuck-deal record for "{titleOf(deleteTarget)}". The underlying deal is
              already gone — this only clears the admin record. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2 text-xs font-medium rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleDelete()}
                disabled={busyId === deleteTarget.id}
                className="flex-1 py-2 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
              >
                {busyId === deleteTarget.id ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
